import { api } from '../../lib/api.ts';
import type { AppProvisioning } from '../invites/api.ts';

export type AppKey = 'wapmeet' | 'wapstat' | 'wapsign' | 'wappro' | 'wapdocs';
import type {
  AppRoleUpdateStatus,
  AppProvisionStatus,
  UpdateAppRolesBody,
  ProvisionAppBody,
  AppRolesResponse,
} from '../appRoles/api.ts';

export type { WapsignRole, WapmeetRole } from '../invites/api.ts';
export type {
  AppRoleUpdateStatus,
  AppProvisionStatus,
  UpdateAppRolesBody,
  ProvisionAppBody,
  AppRolesResponse,
  AppUserRole,
} from '../appRoles/api.ts';

export interface OrgRow {
  id: string;
  name: string;
  nameDv: string | null;
  contactEmail: string;
  status: 'ACTIVE' | 'SUSPENDED';
  wapsignOrgId: string | null;
  wapmeetOrgId: string | null;
  wapstatOrgId: string | null;
  wapproOrgId: string | null;
  wapdocsOrgId: string | null;
  paidUntil: string | null;
  subscriptionNote: string | null;
  createdAt: string;
  _count: { users: number };
}

export interface BootstrapInviteResult {
  invite: {
    id: string;
    email: string;
    isOrgAdmin: boolean;
    expiresAt: string;
    appProvisioning?: AppProvisioning | null;
  };
}

export interface OrgUserRow {
  id: string;
  email: string;
  name: string;
  nameDv: string | null;
  role: 'SYS_ADMIN' | 'USER';
  isOrgAdmin: boolean;
  isActive: boolean;
  designationDv: string | null;
  designationEn: string | null;
  createdAt: string;
}

export interface OrgUserPatch {
  isOrgAdmin?: boolean;
  isActive?: boolean;
  nameDv?: string | null;
  designationDv?: string | null;
  designationEn?: string | null;
}

export interface OrgMappingPatch {
  status?: 'ACTIVE' | 'SUSPENDED';
  wapsignOrgId?: string | null;
  wapmeetOrgId?: string | null;
  wapstatOrgId?: string | null;
  wapproOrgId?: string | null;
  wapdocsOrgId?: string | null;
}

export const orgsApi = {
  list: (page = 1, pageSize = 20) =>
    api
      .get<{ orgs: OrgRow[]; total: number; page: number; pageSize: number }>('/admin/organizations', {
        params: { page, pageSize },
      })
      .then((r) => r.data),
  get: (id: string) => api.get<{ org: OrgRow }>(`/admin/organizations/${id}`).then((r) => r.data.org),
  create: (name: string, nameDv: string, contactEmail: string) =>
    api.post<{ org: OrgRow }>('/admin/organizations', { name, nameDv, contactEmail }).then((r) => r.data),
  patch: (id: string, patch: OrgMappingPatch) =>
    api.patch<{ org: OrgRow }>(`/admin/organizations/${id}`, patch).then((r) => r.data.org),
  updateOrg: (id: string, patch: { name: string; nameDv: string }) =>
    api.patch<{ org: OrgRow }>(`/admin/organizations/${id}/name`, patch).then((r) => r.data.org),
  bootstrapOrgAdmin: (
    orgId: string,
    email: string,
    opts?: { isOrgAdmin?: boolean; appProvisioning?: AppProvisioning },
  ) =>
    api
      .post<BootstrapInviteResult>(`/admin/organizations/${orgId}/invites`, {
        email,
        isOrgAdmin: opts?.isOrgAdmin,
        appProvisioning: opts?.appProvisioning,
      })
      .then((r) => r.data),
  listUsers: (orgId: string) =>
    api.get<{ users: OrgUserRow[] }>(`/admin/organizations/${orgId}/users`).then((r) => r.data.users),
  appRoles: (orgId: string) =>
    api.get<{ roles: AppRolesResponse }>(`/admin/organizations/${orgId}/app-roles`).then((r) => r.data.roles),
  updateUser: (orgId: string, userId: string, patch: OrgUserPatch) =>
    api
      .patch<{ user: OrgUserRow }>(`/admin/organizations/${orgId}/users/${userId}`, patch)
      .then((r) => r.data.user),
  resetUserPassword: (orgId: string, userId: string) =>
    api.post<void>(`/admin/organizations/${orgId}/users/${userId}/reset-password`).then(() => undefined),
  updateAppRoles: (orgId: string, userId: string, body: UpdateAppRolesBody) =>
    api
      .patch<{
        results: {
          wapsign?: AppRoleUpdateStatus;
          wapmeet?: AppRoleUpdateStatus;
          wapstat?: AppRoleUpdateStatus;
          wappro?: AppRoleUpdateStatus;
          wapdocs?: AppRoleUpdateStatus;
        };
      }>(
        `/admin/organizations/${orgId}/users/${userId}/app-roles`,
        body,
      )
      .then((r) => r.data.results),
  provisionApp: (orgId: string, userId: string, body: ProvisionAppBody) =>
    api
      .post<{ result: AppProvisionStatus }>(
        `/admin/organizations/${orgId}/users/${userId}/app-provision`,
        body,
      )
      .then((r) => r.data.result),
  enableApp: (id: string, app: AppKey) =>
    api.post<{ org: OrgRow }>(`/admin/organizations/${id}/apps/${app}/enable`).then((r) => r.data.org),
  disableApp: (id: string, app: AppKey) =>
    api.post<{ org: OrgRow }>(`/admin/organizations/${id}/apps/${app}/disable`).then((r) => r.data.org),
  resync: (orgId: string) =>
    api
      .post<{ summary: ResyncSummary }>(`/admin/organizations/${orgId}/resync`)
      .then((r) => r.data.summary),
};

export interface ResyncSummary {
  users: number;
  wapsign: { ok: number; failed: number; skipped: number };
  wapmeet: { ok: number; failed: number; skipped: number };
  wapstat: { ok: number; failed: number; skipped: number };
  wappro: { ok: number; failed: number; skipped: number };
  wapdocs: { ok: number; failed: number; skipped: number };
}
